import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Showcase {
  label: string;
  title: string;
  body: string;
  bullets: string[];
  visual: React.ReactNode;
}

/* ───── Animated Visual Mockups ───── */

function ImageGridMockup() {
  const [activeIdx, setActiveIdx] = useState(0);
  const gradients = [
    'linear-gradient(135deg, #7C3AED 0%, #3B82F6 100%)',
    'linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)',
    'linear-gradient(135deg, #6D28D9 0%, #34D399 100%)',
    'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
    'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
    'linear-gradient(135deg, #34D399 0%, #3B82F6 100%)',
  ];

  // Cycle through images with a shimmer/glow effect
  useEffect(() => {
    const timer = setInterval(() => setActiveIdx(p => (p + 1) % 6), 1500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        background: '#14142B',
        border: '1px solid rgba(232,230,240,0.06)',
        borderRadius: '12px',
        padding: '24px',
        transition: 'box-shadow 0.3s ease',
      }}
      className="hover:shadow-[0_0_40px_rgba(124,58,237,0.12)]"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        {gradients.map((bg, i) => (
          <motion.div
            key={i}
            animate={{
              scale: activeIdx === i ? 1.05 : 1,
              boxShadow: activeIdx === i ? '0 0 25px rgba(124,58,237,0.5)' : '0 0 0px transparent',
            }}
            transition={{ duration: 0.4 }}
            style={{
              background: bg,
              borderRadius: '8px',
              aspectRatio: '1',
              border: activeIdx === i ? '2px solid rgba(124,58,237,0.7)' : '2px solid rgba(124,58,237,0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Shimmer sweep */}
            {activeIdx === i && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '200%' }}
                transition={{ duration: 0.8, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                }}
              />
            )}
          </motion.div>
        ))}
      </div>
      {/* AI generating indicator */}
      <div style={{ marginTop: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{
            width: '28px', height: '28px', borderRadius: '50%',
            border: '2px solid rgba(124,58,237,0.2)',
            borderTopColor: '#7C3AED',
          }}
        />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85rem', color: '#C8C6D2' }}>
          Generating image {activeIdx + 1}/6...
        </span>
      </div>
    </div>
  );
}

function CarouselMockup() {
  const [activeSlide, setActiveSlide] = useState(1);
  const slides = [
    { bg: 'linear-gradient(135deg, #6D28D9, #3B82F6)', label: 'Slide 1' },
    { bg: 'linear-gradient(135deg, #7C3AED, #06B6D4)', label: 'Slide 2' },
    { bg: 'linear-gradient(135deg, #8B5CF6, #34D399)', label: 'Slide 3' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setActiveSlide(p => (p + 1) % 3), 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      style={{
        background: '#14142B',
        border: '1px solid rgba(232,230,240,0.06)',
        borderRadius: '12px',
        padding: '32px 24px',
        transition: 'box-shadow 0.3s ease',
      }}
      className="hover:shadow-[0_0_40px_rgba(124,58,237,0.12)]"
    >
      <div style={{ position: 'relative', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: '800px' }}>
        {slides.map((slide, i) => {
          const offset = i - activeSlide;
          return (
            <motion.div
              key={i}
              animate={{
                x: offset * 80,
                rotateY: offset * -12,
                scale: offset === 0 ? 1 : 0.85,
                opacity: offset === 0 ? 1 : 0.5,
                zIndex: offset === 0 ? 10 : 1,
              }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'absolute',
                width: '170px',
                height: '220px',
                background: slide.bg,
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: '16px',
                boxShadow: offset === 0 ? '0 12px 40px rgba(124,58,237,0.4)' : 'none',
              }}
            >
              {/* Fake text overlay */}
              <div style={{ height: '8px', width: '80%', background: 'rgba(255,255,255,0.6)', borderRadius: '4px', marginBottom: '8px' }} />
              <div style={{ height: '6px', width: '55%', background: 'rgba(255,255,255,0.35)', borderRadius: '3px' }} />
              {/* Slide number */}
              <div style={{
                position: 'absolute', top: '12px', right: '12px',
                fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.6)', background: 'rgba(0,0,0,0.3)',
                padding: '2px 8px', borderRadius: '4px',
              }}>
                {i + 1}/3
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Dots indicator */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              width: activeSlide === i ? 28 : 8,
              background: activeSlide === i ? '#7C3AED' : 'rgba(232,230,240,0.2)',
            }}
            transition={{ duration: 0.3 }}
            style={{ height: '8px', borderRadius: '4px' }}
          />
        ))}
      </div>
    </div>
  );
}

function AnalyticsMockup() {
  const stats = [
    { label: 'Posts', value: 247, color: '#7C3AED' },
    { label: 'Engagement', value: 89, color: '#34D399', suffix: '%' },
    { label: 'Reach', value: 12400, color: '#3B82F6', format: true },
  ];
  const barHeights = [60, 85, 45, 70, 95];

  return (
    <div
      style={{
        background: '#14142B',
        border: '1px solid rgba(232,230,240,0.06)',
        borderRadius: '12px',
        padding: '24px',
        transition: 'box-shadow 0.3s ease',
      }}
      className="hover:shadow-[0_0_40px_rgba(124,58,237,0.12)]"
    >
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {stats.map((stat, i) => (
          <div key={i} style={{ flex: 1, background: 'rgba(232,230,240,0.04)', borderRadius: '8px', padding: '14px', borderLeft: `3px solid ${stat.color}` }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: '#C8C6D2', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
              {stat.label}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.5rem', fontWeight: 700, color: '#E8E6F0', marginTop: '4px' }}
            >
              {stat.format ? '12.4K' : stat.value}{stat.suffix || ''}
            </motion.div>
          </div>
        ))}
      </div>
      {/* Animated bar chart */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '120px', padding: '0 8px' }}>
        {barHeights.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            style={{
              flex: 1,
              borderRadius: '6px 6px 0 0',
              background: i % 2 === 0
                ? 'linear-gradient(to top, #7C3AED, #8B5CF6)'
                : 'linear-gradient(to top, #34D399, #6EE7B7)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CalendarMockup() {
  const highlightedDays = [2, 5, 8, 11, 15, 18, 22, 25];
  const emeraldDays = [5, 15, 22];
  const ideas = [
    { title: 'Product Launch Post', color: '#7C3AED' },
    { title: 'Industry Trends Thread', color: '#34D399' },
    { title: 'Customer Case Study', color: '#3B82F6' },
  ];

  return (
    <div
      style={{
        background: '#14142B',
        border: '1px solid rgba(232,230,240,0.06)',
        borderRadius: '12px',
        padding: '24px',
        transition: 'box-shadow 0.3s ease',
      }}
      className="hover:shadow-[0_0_40px_rgba(124,58,237,0.12)]"
    >
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <div key={i} style={{ textAlign: 'center' as const, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: 'rgba(232,230,240,0.5)', paddingBottom: '4px' }}>
                {d}
              </div>
            ))}
            {Array.from({ length: 28 }, (_, i) => {
              const day = i + 1;
              const isHighlighted = highlightedDays.includes(day);
              const isEmerald = emeraldDays.includes(day);
              const delayIdx = highlightedDays.indexOf(day);
              return (
                <motion.div
                  key={i}
                  initial={isHighlighted || isEmerald ? { scale: 0, opacity: 0 } : {}}
                  whileInView={isHighlighted || isEmerald ? { scale: 1, opacity: 1 } : {}}
                  viewport={{ once: true }}
                  transition={isHighlighted || isEmerald ? { duration: 0.4, delay: (delayIdx >= 0 ? delayIdx : 0) * 0.08 + 0.3, type: 'spring', stiffness: 400, damping: 15 } : {}}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: '5px',
                    background: isEmerald ? 'rgba(52,211,153,0.35)' : isHighlighted ? 'rgba(124,58,237,0.35)' : 'rgba(232,230,240,0.04)',
                    border: isHighlighted || isEmerald ? `1px solid ${isEmerald ? 'rgba(52,211,153,0.6)' : 'rgba(124,58,237,0.6)'}` : '1px solid transparent',
                  }}
                />
              );
            })}
          </div>
        </div>
        {/* Idea cards that slide in */}
        <div style={{ width: '130px', display: 'flex', flexDirection: 'column' as const, gap: '10px' }}>
          {ideas.map((idea, i) => (
            <motion.div
              key={i}
              initial={{ x: 30, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: 'rgba(232,230,240,0.04)',
                borderRadius: '8px',
                padding: '10px 12px',
                borderLeft: `3px solid ${idea.color}`,
              }}
            >
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '0.8rem', color: '#E8E6F0', lineHeight: 1.3, fontWeight: 500 }}>
                {idea.title}
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: '#C8C6D2', marginTop: '4px' }}>
                AI suggested
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───── Data ───── */

const showcases: Showcase[] = [
  {
    label: 'AI-POWERED VISUALS',
    title: 'Generate Stunning Images for Every Post',
    body: 'Get AI-generated images that perfectly match your brand and message. Choose from stock suggestions, upload your own, or let ZUPA generate context-aware custom images with text overlays.',
    bullets: [
      'Context-aware AI image generation',
      'Custom text overlay editor',
      'Brand-aligned visual suggestions',
    ],
    visual: <ImageGridMockup />,
  },
  {
    label: 'MULTI-SLIDE CONTENT',
    title: 'Build Scroll-Stopping Carousels',
    body: 'Create engaging multi-slide carousel posts with AI-generated images and text overlays. ZUPA plans the narrative arc first, then generates each slide with perfectly matched visuals.',
    bullets: [
      'AI-planned narrative arcs',
      'Slide-by-slide generation',
      'Custom text overlays per slide',
    ],
    visual: <CarouselMockup />,
  },
  {
    label: 'TRACK & OPTIMIZE',
    title: 'Your Content Command Center',
    body: 'Monitor your generation history, track which psychological principles drive the most engagement, and see your content performance across all platforms from a single dashboard.',
    bullets: [
      'Generation history & analytics',
      'Psychology principle effectiveness',
      'Cross-platform performance tracking',
    ],
    visual: <AnalyticsMockup />,
  },
  {
    label: 'PLAN & IDEATE',
    title: 'Never Run Out of Content Ideas',
    body: 'Plan your content strategy with a visual calendar, add notes to specific dates, and get AI-suggested content topics based on your brand, industry trends, and audience preferences.',
    bullets: [
      'Visual content calendar with notes',
      'AI-suggested content topics',
      'Trend-based ideation engine',
    ],
    visual: <CalendarMockup />,
  },
];

/* ───── Main Component ───── */

export function FeatureShowcaseSection() {
  return (
    <>
      {showcases.map((showcase, i) => {
        const isReversed = i % 2 !== 0;
        const bgColor = i % 2 === 0 ? '#06060F' : '#0D0D1A';

        return (
          <section
            key={i}
            style={{
              backgroundColor: bgColor,
              padding: 'clamp(64px, 10vw, 120px) 0',
            }}
          >
            <div
              style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '0 24px',
              }}
            >
              <div
                className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16"
                style={{
                  flexDirection: isReversed ? undefined : undefined,
                }}
              >
                {/* Text side */}
                <motion.div
                  className={`w-full lg:w-1/2 ${isReversed ? 'lg:order-2' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      color: '#7C3AED',
                      display: 'block',
                      marginBottom: '16px',
                    }}
                  >
                    {showcase.label}
                  </span>

                  <h2
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: 'clamp(2rem, 4vw + 0.5rem, 3rem)',
                      fontWeight: 600,
                      color: '#E8E6F0',
                      lineHeight: 1.15,
                      marginBottom: '20px',
                    }}
                  >
                    {showcase.title}
                  </h2>

                  <p
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
                      color: '#C8C6D2',
                      lineHeight: 1.6,
                      maxWidth: '540px',
                      marginBottom: '28px',
                    }}
                  >
                    {showcase.body}
                  </p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {showcase.bullets.map((bullet, j) => (
                      <li
                        key={j}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontSize: '1.2rem',
                          color: '#C8C6D2',
                        }}
                      >
                        <Check
                          size={20}
                          style={{
                            color: '#34D399',
                            flexShrink: 0,
                          }}
                        />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Visual side */}
                <motion.div
                  className={`w-full lg:w-1/2 ${isReversed ? 'lg:order-1' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  {showcase.visual}
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
